import React, { useState, useEffect, useCallback } from 'react'
import { useProgram } from '@/hooks/useProgram'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Avatar,
	AvatarFallback,
} from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
	Loader2,
	X,
	Users,
	Shield,
	Eye,
	Edit3,
	Crown,
	Check,
	Copy,
	RefreshCw,
	Send,
	Sparkles,
	Lock,
	AlertCircle,
	Trash2,
	ChevronDown,
} from 'lucide-react'
import { getAuthHeaders } from '@/services/api'
import { getApiBaseUrl } from '@/lib/api'
import { useTeamContext } from '@/context/TeamContext'
import { useProgramContext } from '@/context/ProgramContext'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubTrigger,
	DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { useTranslation } from '@/hooks/useTranslation'
import { isPaidPlan } from '@/lib/planHelpers'
import { canInviteMembers, isProjectOwner, normalizeAccessLevel } from '@/lib/programPermissions'

interface ProgramInvite {
	id: string
	invitee_email: string
	created_at: string
	expires_at: string
	status: string
	token: string
	accessLevel?: 'read' | 'admin' | 'manager' | 'member'
	invited_by_user?: {
		name: string | null
		email: string
	}
}

interface ProgramMember {
	id: string
	userId: string
	name: string | null
	email: string
	teamRole: string
	hasAccess: boolean
	accessLevel?: 'read' | 'admin' | 'manager' | 'member' | 'owner' | 'write' | null
	grantedAt?: string
	status?: 'pending' | 'accepted'
	inviteId?: string
	accessiblePrograms?: Array<{
		id: string
		name: string
		accessLevel: string
		isCurrent: boolean
	}>
	totalPrograms?: number
}

export function ProgramInvites() {
	const { t } = useTranslation()
	const { selectedProgram } = useProgram()
	const { teamData, role, isLoading: teamLoading } = useTeamContext()
	const { currentProgramAccessLevel, isInvitedProgram } = useProgramContext()
	const { toast } = useToast()

	const [invites, setInvites] = useState<ProgramInvite[]>([])
	const [members, setMembers] = useState<ProgramMember[]>([])
	const [loading, setLoading] = useState(false)
	const [inviteEmail, setInviteEmail] = useState('')
	const [inviteAccessLevel, setInviteAccessLevel] = useState<'read' | 'admin' | 'manager' | 'member'>('member')
	const [sendingInvite, setSendingInvite] = useState(false)
	const [featureAvailable, setFeatureAvailable] = useState<boolean | null>(null)
	const [checkingAvailability, setCheckingAvailability] = useState(true)
	const [activeTab, setActiveTab] = useState('members')
	const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null)
	const [showSeatLimitModal, setShowSeatLimitModal] = useState(false)
	const [seatInfo, setSeatInfo] = useState<{
		seatsIncluded: number
		seatsUsed: number
		seatsAvailable: number
		hasActiveSubscription?: boolean
	} | null>(null)
	const [pendingInviteEmail, setPendingInviteEmail] = useState('')
	const [pendingInviteAccessLevel, setPendingInviteAccessLevel] = useState<'read' | 'admin' | 'manager' | 'member'>('member')
	const [addingSeat, setAddingSeat] = useState(false)
	const [showDeleteDialog, setShowDeleteDialog] = useState(false)
	const [memberToDelete, setMemberToDelete] = useState<ProgramMember | null>(null)

	const canManageMembers = canInviteMembers(currentProgramAccessLevel)
	const isAdmin = canManageMembers

	const checkSeatsAvailability = useCallback(async () => {
		if (!teamData?.id) return

		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/stripe/check-seats/${teamData.id}`,
				{
					headers,
				}
			)

			if (response.ok) {
				const data = await response.json()
			
				setSeatInfo(data)
			}
		} catch (error) {
			console.error('Error checking seats:', error)
		}
	}, [teamData?.id])

	const checkFeatureAvailability = useCallback(async () => {
		setCheckingAvailability(true)
		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/program-invites/check-availability?programId=${selectedProgram?.id || ''}`,
				{
					headers,
				}
			)

			if (response.ok) {
				const data = await response.json()
				setFeatureAvailable(data.available)
			} else {
				setFeatureAvailable(isPaidPlan(teamData?.plan_id))
			}
		} catch (error) {
			setFeatureAvailable(isPaidPlan(teamData?.plan_id))
		} finally {
			setCheckingAvailability(false)
		}
	}, [teamData?.plan_id, selectedProgram?.id])

	const fetchInvites = useCallback(async () => {
		if (!selectedProgram?.id) return

		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/program-invites/program/${selectedProgram.id}`,
				{
					headers,
				}
			)

			if (response.ok) {
				const data = await response.json()
				setInvites(data)
			}
		} catch (error) {
			// Error handling for fetch invites
		}
	}, [selectedProgram?.id])

	const fetchMembers = useCallback(async () => {
		if (!selectedProgram?.id) return

		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/program-invites/members/${selectedProgram.id}`,
				{
					headers,
				}
			)

			if (response.ok) {
				const data = await response.json()
				setMembers(data)
			}
		} catch (error) {
			// Error handling for fetch members
		}
	}, [selectedProgram?.id])

	useEffect(() => {
		checkFeatureAvailability()
		if (selectedProgram?.id) {
			fetchInvites()
			fetchMembers()
			checkSeatsAvailability()
		}
	}, [
		checkFeatureAvailability,
		checkSeatsAvailability,
		fetchInvites,
		fetchMembers,
		selectedProgram?.id,
	])

	const isValidEmail = (email: string) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return emailRegex.test(email)
	}

	const handleAddSeatAndInvite = async () => {
		setAddingSeat(true)
		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/stripe/add-seat`,
				{
					method: 'POST',
					headers: {
						...headers,
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						teamId: teamData?.id,
					}),
				}
			)

			if (response.ok) {
				await checkSeatsAvailability()
				setShowSeatLimitModal(false)

				const email = pendingInviteEmail
				const accessLevel = pendingInviteAccessLevel
				setPendingInviteEmail('')

				setSendingInvite(true)
				const inviteResponse = await fetch(
					`${getApiBaseUrl()}/api/program-invites/send`,
					{
						method: 'POST',
						headers: {
							...headers,
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							email: email,
							programId: selectedProgram?.id,
							accessLevel: accessLevel,
						}),
					}
				)

				const inviteData = await inviteResponse.json()

				if (inviteResponse.ok) {
					toast({
						title: t('programInvites.seatAddedAndInviteSent'),
						description: t('programInvites.invitationSentTo', { email }),
					})
					setInviteEmail('')
					fetchInvites()
					fetchMembers()
				} else {
					toast({
						title: t('programInvites.error'),
						description:
							inviteData.error || t('programInvites.failedToSendInvitation'),
						variant: 'destructive',
					})
				}
				setSendingInvite(false)
			} else {
				const data = await response.json()
				toast({
					title: t('common.error'),
					description: data.error || t('programInvites.failedToAddSeat'),
					variant: 'destructive',
				})
			}
		} catch (error) {
			toast({
				title: t('common.error'),
				description: t('programInvites.failedToAddSeat'),
				variant: 'destructive',
			})
		} finally {
			setAddingSeat(false)
		}
	}

	const sendInvite = async () => {
		if (!selectedProgram?.id || !inviteEmail) return

		if (!isValidEmail(inviteEmail)) {
			toast({
				title: t('programInvites.invalidEmail'),
				description: t('programInvites.pleaseEnterValidEmailAddress'),
				variant: 'destructive',
			})
			return
		}

		setSendingInvite(true)
		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/program-invites/send`,
				{
					method: 'POST',
					headers: {
						...headers,
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						email: inviteEmail,
						programId: selectedProgram.id,
						accessLevel: inviteAccessLevel,
					}),
				}
			)

			const data = await response.json()

			if (response.ok) {
				toast({
					title: data.immediate ? t('programInvites.accessGranted') : t('programInvites.invitationSent'),
					description: data.immediate
						? t('programInvites.alreadyHadTeamAccess', { email: inviteEmail })
						: t('programInvites.invitationSentTo', { email: inviteEmail }),
				})
				setInviteEmail('')
				fetchInvites()
				fetchMembers()
				await checkSeatsAvailability()
			} else {
				if (data.requiresSeatUpgrade) {
					setSeatInfo({
						seatsIncluded: data.seatsIncluded ?? seatInfo?.seatsIncluded ?? 0,
						seatsUsed: data.seatsUsed ?? seatInfo?.seatsUsed ?? 0,
						seatsAvailable:
							data.seatsAvailable ?? seatInfo?.seatsAvailable ?? 0,
						hasActiveSubscription: true,
					})
					setPendingInviteEmail(inviteEmail)
					setPendingInviteAccessLevel(
						inviteAccessLevel as 'read' | 'admin' | 'manager' | 'member'
					)
					setShowSeatLimitModal(true)
				} else if (data.requiresUpgrade) {
					toast({
						title: t('programInvites.paidPlanRequired'),
						description: data.upgradeMessage || data.error,
						variant: 'destructive',
						action: (
							<Button
								variant="default"
								size="sm"
								onClick={() => window.location.href = '/settings/billing'}
							>
								{t('programInvites.upgradePlan')}
							</Button>
						),
					})
				} else {
					toast({
						title: t('programInvites.error'),
						description: data.error || t('programInvites.failedToSendInvitation'),
						variant: 'destructive',
					})
				}
			}
		} catch (error) {
			toast({
				title: t('programInvites.error'),
				description: t('programInvites.failedToSendInvitation'),
				variant: 'destructive',
			})
		} finally {
			setSendingInvite(false)
		}
	}

	const cancelInvite = async (inviteId: string) => {
		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/program-invites/${inviteId}`,
				{
					method: 'DELETE',
					headers,
				}
			)

			if (response.ok) {
				toast({
					title: t('programInvites.invitationCancelled'),
					description: t('programInvites.invitationHasBeenCancelled'),
				})
				fetchInvites()
			}
		} catch (error) {
			toast({
				title: t('programInvites.error'),
				description: t('programInvites.failedToCancelInvitation'),
				variant: 'destructive',
			})
		}
	}

	const toggleAccess = async (member: ProgramMember, accessLevel: 'read' | 'admin' | 'manager' | 'member' | null = 'member') => {
		if (!selectedProgram?.id) return

		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/program-invites/access`,
				{
					method: 'POST',
					headers: {
						...headers,
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						programId: selectedProgram.id,
						teamMemberId: member.id,
						grant: accessLevel !== null,
						accessLevel: accessLevel || 'member',
					}),
				}
			)

			const data = await response.json()

			if (response.ok) {
				const title = accessLevel === null
					? t('programInvites.accessRevoked')
					: member.hasAccess
						? t('programInvites.accessLevelUpdated')
						: t('programInvites.accessGranted')

				const description = accessLevel === null
					? t('programInvites.noLongerHasAccess', { name: member.name || member.email })
					: t('programInvites.nowHasAccess', { name: member.name || member.email, level: accessLevel })

				toast({
					title,
					description,
				})
				fetchMembers()
				await checkSeatsAvailability()
			} else if (data.requiresSeatUpgrade) {
				setSeatInfo({
					seatsIncluded: data.seatsIncluded ?? seatInfo?.seatsIncluded ?? 0,
					seatsUsed: data.seatsUsed ?? seatInfo?.seatsUsed ?? 0,
					seatsAvailable: data.seatsAvailable ?? seatInfo?.seatsAvailable ?? 0,
					hasActiveSubscription: true,
				})
				toast({
					title: t('programInvites.collaboratorLimitReached'),
					description:
						data.error ||
						t('programInvites.seatLimitDescription', {
							seatsIncluded: data.seatsIncluded ?? seatInfo?.seatsIncluded ?? 0,
							seatsUsed: data.seatsUsed ?? seatInfo?.seatsUsed ?? 0,
						}),
					variant: 'destructive',
				})
			} else if (data.requiresUpgrade) {
				toast({
					title: t('programInvites.paidPlanRequired'),
					description: data.upgradeMessage || data.error,
					variant: 'destructive',
					action: (
						<Button
							variant="default"
							size="sm"
							onClick={() => window.location.href = '/settings/billing'}
						>
							{t('programInvites.upgradePlan')}
						</Button>
					),
				})
			}
		} catch (error) {
			toast({
				title: t('programInvites.error'),
				description: t('programInvites.failedToUpdateAccess'),
				variant: 'destructive',
			})
		}
	}

	const updateTeamRole = async (member: ProgramMember, newRole: string) => {
		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/team/members/${member.id}/role`,
				{
					method: 'PUT',
					headers: {
						...headers,
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ role: newRole }),
				}
			)

			if (response.ok) {
				toast({
					title: t('programInvites.roleUpdated'),
					description: t('programInvites.roleUpdatedSuccessfully', { name: member.name || member.email, role: newRole }),
				})
				fetchMembers()
			} else {
				const data = await response.json()
				toast({
					title: t('programInvites.error'),
					description: data.error || t('programInvites.failedToUpdateRole'),
					variant: 'destructive',
				})
			}
		} catch (error) {
			toast({
				title: t('programInvites.error'),
				description: t('programInvites.failedToUpdateRole'),
				variant: 'destructive',
			})
		}
	}

	const handleDeleteClick = (member: ProgramMember) => {
		setMemberToDelete(member)
		setShowDeleteDialog(true)
	}

	const confirmDeleteMember = async () => {
		if (!memberToDelete) return

		try {
			const headers = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/program-invites/remove-member/${memberToDelete.id}?programId=${selectedProgram?.id}`,
				{
					method: 'DELETE',
					headers,
				}
			)

			if (response.ok) {
				toast({
					title: t('programInvites.memberRemoved'),
					description: t('programInvites.memberRemovedSuccessfully', { name: memberToDelete.name || memberToDelete.email }),
				})
				fetchMembers()
				setShowDeleteDialog(false)
				setMemberToDelete(null)
			} else {
				const data = await response.json()
				toast({
					title: t('programInvites.error'),
					description: data.error || t('programInvites.failedToRemoveMember'),
					variant: 'destructive',
				})
			}
		} catch (error) {
			toast({
				title: t('programInvites.error'),
				description: t('programInvites.failedToRemoveMember'),
				variant: 'destructive',
			})
		}
	}

	const copyInviteLink = async (inviteId: string) => {
		// Find the invite to get the token
		const invite = invites.find(i => i.id === inviteId)
		if (!invite) {
			toast({
				title: t('programInvites.error'),
				description: t('programInvites.couldNotFindInviteDetails'),
				variant: 'destructive'
			})
			return
		}

		const inviteUrl = `${window.location.origin}/invite/program/${invite.token}`
		await navigator.clipboard.writeText(inviteUrl)
		setCopiedInviteId(inviteId)
		toast({
			title: t('programInvites.linkCopied'),
			description: t('programInvites.invitationLinkCopiedToClipboard'),
		})
		setTimeout(() => setCopiedInviteId(null), 2000)
	}

	const resendInvite = async (invite: ProgramInvite) => {
		// Cancel old invite and create new one
		await cancelInvite(invite.id)
		setInviteEmail(invite.invitee_email)
		// Trigger send after setting email
		setTimeout(() => sendInvite(), 100)
	}





	if (teamLoading || checkingAvailability) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{t('programInvites.programAccess')}</CardTitle>
					<CardDescription>
						{t('programInvites.loadingTeamInformation')}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex justify-center py-8">
						<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
					</div>
				</CardContent>
			</Card>
		)
	}

	if (!selectedProgram) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{t('programInvites.programAccess')}<Badge variant="secondary" className="gap-1">
						<Crown className="h-3 w-3" />
						{t('programInvites.paidPlanFeature')}
					</Badge>
					</CardTitle>
					<CardDescription>
						{t('programInvites.selectProgramToManageAccess')}
					</CardDescription>
				</CardHeader>
			</Card>
		)
	}


	const membersWithAccess = members.filter((m) => m.hasAccess)
	const membersWithoutAccess = members.filter((m) => !m.hasAccess)

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>{t('programInvites.teamMembers')}</CardTitle>
							<CardDescription>
								{t('programInvites.manageTeamMemberAccess', { programName: selectedProgram?.name })}
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					{!featureAvailable && !isInvitedProgram ? (
						<div className="rounded-lg border bg-gradient-to-br from-primary/5 to-primary/10 p-6 mb-6">
							<div className="flex items-start gap-4">
								<div className="p-2 rounded-full bg-primary/20">
									<Lock className="h-4 w-4 text-primary" />
								</div>
								<div className="flex-1 space-y-3">
									<h4 className="font-semibold">{t('programInvites.upgradeToProToUnlock')}</h4>
									<p className="text-sm text-muted-foreground">
										{t('programInvites.withPaidPlanYouCanInviteTeamMembers')}
									</p>
									<ul className="space-y-2 text-sm text-muted-foreground">
										<li className="flex items-start gap-2">
											<Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
											<span>{t('programInvites.inviteUnlimitedTeamMembers')}</span>
										</li>
										<li className="flex items-start gap-2">
											<Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
											<span>{t('programInvites.setReadOnlyWriteOrAdminAccess')}</span>
										</li>
										<li className="flex items-start gap-2">
											<Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
											<span>{t('programInvites.controlWhoCanViewAndEdit')}</span>
										</li>
									</ul>
									<div className="pt-2">
										<Button
											onClick={() => window.location.href = '/settings?tab=billing'}
											className="gap-2"
										>
											<Sparkles className="h-4 w-4" />
											{t('programInvites.upgradePlan')}
										</Button>
									</div>
								</div>
							</div>
						</div>
					) : isAdmin ? (
						<div className="space-y-4 mb-6">
							<div className="flex gap-3">
								<div className="flex-1">
									<Input
										id="email"
										type="email"
										placeholder={t('programInvites.enterEmailToSendInvite')}
										value={inviteEmail}
										onChange={(e) => setInviteEmail(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' && inviteEmail && featureAvailable) {
												sendInvite()
											}
										}}
										disabled={!featureAvailable}
									/>
								</div>
								<select
									id="access-level"
									className="w-40 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
									value={inviteAccessLevel}
									onChange={(e) => setInviteAccessLevel(e.target.value as 'read' | 'admin' | 'manager' | 'member')}
									disabled={!featureAvailable}
								>
									<option value="admin">{t('programInvites.admin')}</option>
									<option value="manager">{t('programInvites.manager')}</option>
									<option value="member">{t('programInvites.member')}</option>
									<option value="read">{t('programInvites.readOnly')}</option>
								</select>
								<Button
									onClick={sendInvite}
									disabled={!featureAvailable || !inviteEmail || sendingInvite}
									className="gap-2"
								>
									{sendingInvite ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<Send className="h-4 w-4" />
									)}
									{t('programInvites.sendInvite')}
								</Button>
							</div>
							<div className="p-4 bg-muted/50 rounded-lg space-y-3">
								<h4 className="text-sm font-medium">{t('programInvites.roleDescriptions', 'Role Descriptions')}</h4>
								<div className="space-y-2 text-sm">
									<div>
										<div className="flex items-center gap-2 mb-1">
											<Shield className="h-4 w-4 text-primary" />
											<span className="font-medium">{t('programInvites.admin')}</span>
										</div>
										<p className="text-muted-foreground ml-6">
											{t('programInvites.roleDescription.admin')}
										</p>
									</div>
									<div>
										<div className="flex items-center gap-2 mb-1">
											<Shield className="h-4 w-4 text-primary" />
											<span className="font-medium">{t('programInvites.manager')}</span>
										</div>
										<p className="text-muted-foreground ml-6">
											{t('programInvites.roleDescription.manager')}
										</p>
									</div>
									<div>
										<div className="flex items-center gap-2 mb-1">
											<Eye className="h-4 w-4 text-primary" />
											<span className="font-medium">{t('programInvites.readOnly')}</span>
										</div>
										<p className="text-muted-foreground ml-6">
											{t('programInvites.roleDescription.readOnly')}
										</p>
									</div>
									<div>
										<div className="flex items-center gap-2 mb-1">
											<Users className="h-4 w-4 text-primary" />
											<span className="font-medium">{t('programInvites.member')}</span>
										</div>
										<p className="text-muted-foreground ml-6">
											{t('programInvites.roleDescription.member')}
										</p>
									</div>
								</div>
							</div>
						</div>
					) : (
						<p className="text-sm text-muted-foreground mb-6">
							{t('programInvites.onlyAdministratorsCanInvite')}
						</p>
					)}

					{/* Members Table */}
					<div className="overflow-hidden rounded-lg border border-border/50 bg-white dark:bg-slate-900">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t('programInvites.member')}</TableHead>
									<TableHead>{t('programInvites.teamRole')}</TableHead>
									<TableHead>{t('programInvites.accessToThisProgram')}</TableHead>
									<TableHead className="text-right">{t('programInvites.actions')}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{members.length === 0 ? (
									<TableRow>
										<TableCell colSpan={4} className="text-center py-8">
											<Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
											<p className="text-sm text-muted-foreground">{t('programInvites.noTeamMembersYet')}</p>
											{isAdmin && (
												<p className="text-sm text-muted-foreground mt-2">
													{t('programInvites.clickSendInviteToGetStarted')}
												</p>
											)}
										</TableCell>
									</TableRow>
								) : (
									[...members, ...invites.map(invite => ({
										id: invite.id,
										userId: '',
										name: null,
										email: invite.invitee_email,
										teamRole: 'member',
										hasAccess: false,
										accessLevel: invite.accessLevel,
										grantedAt: invite.created_at,
										status: 'pending' as const,
										inviteId: invite.id,
										inviteToken: invite.token,
										accessiblePrograms: [{
											id: selectedProgram?.id || '',
											name: selectedProgram?.name || '',
											accessLevel: invite.accessLevel,
											isCurrent: true
										}],
										totalPrograms: 1
									}))].map((member) => {
										const getAccessLevelLabel = () => {
											const accessLevel = normalizeAccessLevel(
												member.accessLevel as 'read' | 'admin' | 'manager' | 'member' | 'owner' | 'write' | null | undefined
											)
											if (accessLevel === 'owner') return t('programInvites.owner')
											if (accessLevel === 'admin') return t('programInvites.admin')
											if (accessLevel === 'manager') return t('programInvites.manager')
											if (accessLevel === 'read') return t('programInvites.readOnly')
											if (accessLevel === 'member') return t('programInvites.member')
											return t('programInvites.member')
										}
										
										const permissionLabel = getAccessLevelLabel()

										const getTeamRoleLabel = (role: string) => {
											switch (role) {
												case 'owner': return t('programInvites.owner')
												case 'admin': return t('programInvites.admin')
												case 'manager': return t('programInvites.manager')
												case 'member': return t('programInvites.member')
												default: return role
											}
										}

										const getRoleDescription = (role: string) => {
											const roleKey = role === 'read' ? 'readOnly' : role
											return t(`programInvites.roleDescription.${roleKey}`, { defaultValue: '' })
										}

										return (
											<TableRow key={member.id}>
												<TableCell>
													<div className="flex items-center gap-3">
														<Avatar className="h-8 w-8">
															<AvatarFallback className="text-xs font-medium">
																{(member.name?.substring(0, 2) || member.email.substring(0, 2)).toUpperCase()}
															</AvatarFallback>
														</Avatar>
														<div>
															<div className="font-medium">
																{member.name || member.email.split('@')[0]}
															</div>
															<div className="text-sm text-muted-foreground">
																{member.email}
															</div>
														</div>
													</div>
												</TableCell>
												<TableCell>
													{canManageMembers && !isProjectOwner(member.accessLevel) && member.status !== 'pending' ? (
														<DropdownMenu>
															<DropdownMenuTrigger asChild>
																<Button
																	variant="ghost"
																	size="sm"
																	className="h-auto p-0 font-normal hover:bg-transparent"
																>
																	<Badge
																		variant={member.accessLevel === 'owner' || member.accessLevel === 'admin' ? 'default' :
																			member.accessLevel === 'manager' ? 'secondary' : 'outline'}
																		className="font-normal cursor-pointer flex items-center gap-1"
																		title={getRoleDescription(normalizeAccessLevel(member.accessLevel) || 'member')}
																	>
																		{getAccessLevelLabel()}
																		<ChevronDown className="h-3 w-3" />
																	</Badge>
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent align="start">
																{member.accessLevel !== 'admin' && (
																	<DropdownMenuItem
																		onClick={() => toggleAccess(member, 'admin')}
																	>
																		<Shield className="mr-2 h-4 w-4" />
																		{t('programInvites.admin')}
																	</DropdownMenuItem>
																)}
																{member.accessLevel !== 'manager' && (
																	<DropdownMenuItem
																		onClick={() => toggleAccess(member, 'manager')}
																	>
																		<Shield className="mr-2 h-4 w-4" />
																		{t('programInvites.manager')}
																	</DropdownMenuItem>
																)}
																{member.accessLevel !== 'member' && (
																	<DropdownMenuItem
																		onClick={() => toggleAccess(member, 'member')}
																	>
																		<Users className="mr-2 h-4 w-4" />
																		{t('programInvites.member')}
																	</DropdownMenuItem>
																)}
																{member.accessLevel !== 'read' && (
																	<DropdownMenuItem
																		onClick={() => toggleAccess(member, 'read')}
																	>
																		<Eye className="mr-2 h-4 w-4" />
																		{t('programInvites.readOnly')}
																	</DropdownMenuItem>
																)}
															</DropdownMenuContent>
														</DropdownMenu>
													) : (
														<div className="space-y-1">
															<Badge
																variant={member.accessLevel === 'owner' || member.accessLevel === 'admin' ? 'default' :
																	member.accessLevel === 'manager' ? 'secondary' : 'outline'}
																className="font-normal"
																title={getRoleDescription(normalizeAccessLevel(member.accessLevel) || 'member')}
															>
																{getAccessLevelLabel()}
															</Badge>
														</div>
													)}
												</TableCell>
												<TableCell>
													{member.status === 'pending' ? (
														<Badge variant="secondary" className="font-normal text-xs">
															{t('programInvites.pendingInvite')}
														</Badge>
													) : (
														<div className="flex items-center gap-1">
															<span className="text-sm">{t('programInvites.hasAccessToThisProgram')}</span>
														</div>
													)}
												</TableCell>
												<TableCell className="text-right">
													{member.status === 'pending' ? (
														<Badge variant="outline" className="text-yellow-600 border-yellow-600">
															{t('programInvites.pending')}
														</Badge>
													) : (
														<span className="text-sm text-muted-foreground">{t('programInvites.active')}</span>
													)}
													{canManageMembers && (
														member.status === 'pending' ? (
															<div className="flex items-center justify-end gap-1 mt-2">
																<Button
																	variant="ghost"
																	size="sm"
																	onClick={() => copyInviteLink(member.inviteId!)}
																	title={copiedInviteId === member.inviteId ? t('programInvites.copied') : t('programInvites.copyInviteLink')}
																	className={copiedInviteId === member.inviteId ? "text-green-600 hover:text-green-700" : ""}
																>
																	{copiedInviteId === member.inviteId ? (
																		<Check className="h-4 w-4" />
																	) : (
																		<Copy className="h-4 w-4" />
																	)}
																</Button>
																<Button
																	variant="ghost"
																	size="sm"
																	onClick={() => resendInvite(invites.find(i => i.id === member.inviteId)!)}
																	title={t('programInvites.resendInvite')}
																>
																	<RefreshCw className="h-4 w-4" />
																</Button>
																<Button
																	variant="ghost"
																	size="sm"
																	onClick={() => cancelInvite(member.inviteId!)}
																	className="text-destructive hover:text-destructive"
																	title={t('programInvites.cancelInvite')}
																>
																	<X className="h-4 w-4" />
																</Button>
															</div>
														) : !isProjectOwner(member.accessLevel) ? (
															<Button
																variant="ghost"
																size="sm"
																onClick={() => handleDeleteClick(member)}
																className="text-destructive hover:text-destructive"
																title={t('programInvites.removeMember')}
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														) : null
													)}
												</TableCell>
											</TableRow>
										)
									})
								)}
							</TableBody>
						</Table>
					</div>

					<div className="flex items-center justify-between mt-4 text-sm">
						<Button variant="outline" size="sm" disabled>
							{t('programInvites.previous')}
						</Button>
						<span className="text-muted-foreground">{t('programInvites.pageNumber', { current: 1, total: 1 })}</span>
						<Button variant="outline" size="sm" disabled>
							{t('programInvites.next')}
						</Button>
					</div>
				</CardContent>
			</Card>

			<Dialog open={showSeatLimitModal} onOpenChange={setShowSeatLimitModal}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<AlertCircle className="h-5 w-5 text-amber-500" />
							{t('programInvites.collaboratorLimitReached')}
						</DialogTitle>
						<DialogDescription className="space-y-3 pt-2">
							<p>
								{t('programInvites.seatLimitDescription', {
									seatsIncluded: seatInfo?.seatsIncluded || 0,
									seatsUsed: seatInfo?.seatsUsed || 0,
								})}
							</p>
							<p className="font-medium text-foreground">
								{t('programInvites.addSeatConfirmation')}
							</p>
							<p className="text-sm">
								{t('programInvites.prorataExplanation')}
							</p>
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="gap-2">
						<Button
							variant="outline"
							onClick={() => {
								setShowSeatLimitModal(false)
								setPendingInviteEmail('')
							}}
							disabled={addingSeat}
						>
							{t('common.cancel')}
						</Button>
						<Button
							onClick={handleAddSeatAndInvite}
							disabled={addingSeat}
							className="gap-2"
						>
							{addingSeat ? (
								<>
									<Loader2 className="h-4 w-4 animate-spin" />
									{t('programInvites.addingSeat')}
								</>
							) : (
								<>
									{t('programInvites.addSeatAndInvite')}
								</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<AlertCircle className="h-5 w-5 text-destructive" />
							{t('programInvites.removeMember')}
						</DialogTitle>
						<DialogDescription className="pt-2">
							{t('programInvites.confirmRemoveMember', { name: memberToDelete?.name || memberToDelete?.email || '' })}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="gap-2">
						<Button
							variant="outline"
							onClick={() => {
								setShowDeleteDialog(false)
								setMemberToDelete(null)
							}}
						>
							{t('common.cancel')}
						</Button>
						<Button
							variant="destructive"
							onClick={confirmDeleteMember}
							className="gap-2"
						>
							<Trash2 className="h-4 w-4" />
							{t('programInvites.removeMember')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	)
}
